/**
 * Role hierarchy (highest → lowest):
 *   ripik_admin > ripik_user > client_admin > client_user
 *
 * Permissions are deployment-scoped: a user may have different roles
 * across deployments. `assignments` is the array from authStore.
 */

export const ROLES = {
  RIPIK_ADMIN: 'ripik_admin',
  RIPIK_USER: 'ripik_user',
  CLIENT_ADMIN: 'client_admin',
  CLIENT_USER: 'client_user',
};

const ROLE_RANK = {
  [ROLES.RIPIK_ADMIN]: 4,
  [ROLES.RIPIK_USER]: 3,
  [ROLES.CLIENT_ADMIN]: 2,
  [ROLES.CLIENT_USER]: 1,
};

/** Highest role across all assignments (or null if none). */
export function getTopRole(assignments = []) {
  if (!assignments.length) return null;
  return assignments.reduce((best, a) => {
    if (!best) return a.role;
    return (ROLE_RANK[a.role] ?? 0) > (ROLE_RANK[best] ?? 0) ? a.role : best;
  }, null);
}

/** Role for a specific deploymentId (or null). */
export function getRoleForDeployment(assignments = [], deploymentId) {
  const match = assignments.find((a) => a.deploymentId === deploymentId);
  return match?.role ?? null;
}

export function isRipikStaff(assignments = []) {
  return assignments.some(
    (a) => a.role === ROLES.RIPIK_ADMIN || a.role === ROLES.RIPIK_USER
  );
}

export function isRipikAdmin(assignments = []) {
  return assignments.some((a) => a.role === ROLES.RIPIK_ADMIN);
}

export function canManageUsers(assignments = []) {
  return assignments.some(
    (a) => a.role === ROLES.RIPIK_ADMIN || a.role === ROLES.CLIENT_ADMIN
  );
}

export function canViewDeployment(assignments = [], deploymentId) {
  if (isRipikStaff(assignments)) return true;
  return assignments.some((a) => a.deploymentId === deploymentId);
}

/**
 * Returns true when the user should land on Settings (Users/Parameters/Layers)
 * after clicking View on a deployment.
 *
 * ripik_admin  → settings (full management)
 * client_admin → settings (internal CRUD: users, params, layers)
 * ripik_user   → dashboard only
 * client_user  → dashboard only
 */
export function canViewSettings(assignments = [], deploymentId) {
  const role = getRoleForDeployment(assignments, deploymentId);
  if (isRipikAdmin(assignments)) return true;
  return role === ROLES.CLIENT_ADMIN;
}
